import React from 'react';
import {
  AgnesAlert,
  AgnesAlertContent,
  AgnesBadge,
  AgnesButton,
  AgnesSpinner,
  AgnesTooltip,
} from '@agnes/ui';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  ChevronLeft,
  Info,
  Layers,
  ShieldCheck,
} from 'lucide-react';
import { BaseComponentProps } from '@/components/BaseComponentProps';
import { ExportButton } from '@/components/ExportButton';
import { SectionCard } from '@/components/ui/SectionCard';
import { GRAUS_RISCO } from '@/constants/ConstantGrauRiscoRubrica';
import { exportarCsv } from '@/lib/csv';
import { Rubrica } from '@/types/rubricas/Rubrica';
import type {
  PayloadAcaoEmMassa,
  ProcessamentoLoteItem,
} from '@/types/rubricas/analise-lote/ProcessamentoLote';
import {
  type AlteracaoValor,
  type LinhaRevisao,
  linhaRevisao,
  ORIGEM_SELECAO_MULTIPLA,
} from '@/pages/folha-pagamento/acao-em-massa/acaoEmMassa';
import type { EstadoPrevia } from '@/pages/folha-pagamento/acao-em-massa/usePreviaProcessamento';
import { BadgeGrauExposicaoRisco } from '@/components/analise/BadgeGrauExposicaoRisco';
import { AnaliseGrauExposicaoRisco } from '@/types/analise/AnaliseGrauExposicaoRisco';

export interface EtapaRevisaoProps extends BaseComponentProps {
  origem: string;
  rubricas: Rubrica[];
  payload: PayloadAcaoEmMassa;
  campos: string[];
  removidas: { rubricas: number; naturezas: string[] };
  /** Prévia do servidor; "ociosa" quando a ação só aprova (sem campos alterados). */
  previa: EstadoPrevia;
  nomeEmpregador: (cnpj: string | null) => string | undefined;
  confirmando: boolean;
  onAtualizarPrevia: () => void;
  onVoltar: () => void;
  onConfirmar: () => void;
}

const TH = 'whitespace-nowrap p-3 font-semibold';
const TD = 'p-3 align-middle';
const LIMITE_EMPREGADOR = 22;

function truncar(texto: string, limite: number) {
  return texto.length > limite ? `${texto.slice(0, limite)}…` : texto;
}

function Novo({ children }: { children: React.ReactNode }) {
  return (
    <b className="rounded bg-agnes-primary-light px-1.5 py-0.5 text-agnes-primary">{children}</b>
  );
}

function Seta() {
  return <ArrowRight aria-label="para" className="h-3.5 w-3.5 shrink-0 text-agnes-primary" />;
}

function DePara({ valor }: { valor: AlteracaoValor }) {
  if (valor.novo === undefined) {
    return <span className="text-agnes-text-muted">{valor.antigo} · mantido</span>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-agnes-text-muted">{valor.antigo}</span>
      <Seta />
      <Novo>{valor.novo}</Novo>
    </span>
  );
}

function Incidencias({ linha }: { linha: LinhaRevisao }) {
  return (
    <span className="flex flex-wrap items-center gap-1 font-mono text-xs">
      {linha.incidencias.map((incidencia) =>
        incidencia.novo === undefined ? (
          <span key={incidencia.sigla} className="rounded bg-agnes-bg-muted px-1.5 py-0.5">
            {incidencia.sigla} {incidencia.antigo}
          </span>
        ) : (
          <span key={incidencia.sigla} className="inline-flex items-center gap-0.5">
            <span className="text-agnes-text-muted">
              {incidencia.sigla} {incidencia.antigo}
            </span>
            <Seta />
            <Novo>{incidencia.novo}</Novo>
          </span>
        ),
      )}
    </span>
  );
}

const RESULTADOS = {
  APTO: { rotulo: 'Apto', variante: 'success' },
  INAPTO: { rotulo: 'Inapto', variante: 'warning' },
  ERRO: { rotulo: 'Erro', variante: 'error' },
  PENDENTE: { rotulo: 'Preparando…', variante: 'secondary' },
} as const;

function Resultado({ item, dataQa }: { item?: ProcessamentoLoteItem; dataQa: string }) {
  const resultado = RESULTADOS[item?.statusPreparacao ?? 'PENDENTE'];
  const badge = (
    <AgnesBadge
      dataQa={dataQa}
      size="sm"
      variant={resultado.variante}
      className="gap-1 whitespace-nowrap"
    >
      {resultado === RESULTADOS.APTO && <Check className="h-3 w-3" />}
      {resultado.rotulo}
    </AgnesBadge>
  );
  // O gatilho do tooltip recebe ref e o AgnesBadge não repassa: vai num <span>.
  return item?.erroDetalhe ? (
    <AgnesTooltip content={item.erroDetalhe}>
      <span className="inline-flex">{badge}</span>
    </AgnesTooltip>
  ) : (
    badge
  );
}

/** Grau que será aplicado: o manual escolhido, senão o calculado pelo worker na prévia. */
function grauNovo(
  payload: PayloadAcaoEmMassa,
  item: ProcessamentoLoteItem | undefined,
): string | undefined {
  if (payload.grauRisco) return payload.grauRisco;
  const risco = item?.estadoProposto?.risk;
  return typeof risco === 'string' && GRAUS_RISCO.some((grau) => grau.value === risco)
    ? risco
    : undefined;
}

function GrauRisco({
  atual,
  novo,
  calculando,
  dataQa,
}: {
  atual: string | null;
  novo?: string;
  calculando: boolean;
  dataQa: string;
}) {
  const selo = (texto: string, dica: string) => (
    <AgnesTooltip content={dica}>
      <span className="inline-flex">
        <AgnesBadge dataQa={`${dataQa}-situacao`} size="sm" variant="secondary">
          {texto}
        </AgnesBadge>
      </span>
    </AgnesTooltip>
  );
  return (
    <span className="flex items-center gap-1.5">
      <BadgeGrauExposicaoRisco grau={atual as AnaliseGrauExposicaoRisco} />
      {novo && novo !== atual ? (
        <>
          <Seta />
          <BadgeGrauExposicaoRisco
            dataQa={`${dataQa}-novo`}
            grau={novo as AnaliseGrauExposicaoRisco}
          />
        </>
      ) : novo ? (
        selo('mantido', 'O grau de risco calculado é o mesmo de hoje')
      ) : calculando ? (
        selo('calculando…', 'O grau de risco sai da prévia preparada pela Agnes')
      ) : (
        selo('automático', 'Recalculado pelas regras automáticas da Agnes na aplicação')
      )}
    </span>
  );
}

export function EtapaRevisao({
  origem,
  rubricas,
  payload,
  campos,
  removidas,
  previa,
  nomeEmpregador,
  confirmando,
  onAtualizarPrevia,
  onVoltar,
  onConfirmar,
  dataQa = 'acao-massa-revisao',
}: EtapaRevisaoProps) {
  const somenteAprovacao = campos.length === 0;
  const linhas = React.useMemo(
    () => rubricas.map((rubrica) => linhaRevisao(rubrica, payload)),
    [payload, rubricas],
  );
  const itens = previa.fase === 'pronta' ? previa.itens : undefined;
  const aptas = itens ? [...itens.values()].filter((i) => i.statusPreparacao === 'APTO').length : 0;
  const naoAptas = itens ? itens.size - aptas : 0;
  const podeConfirmar = previa.fase === 'pronta' && aptas > 0;
  const calculando = previa.fase === 'preparando' || previa.fase === 'ociosa';
  const total = `${rubricas.length.toLocaleString('pt-BR')} ${rubricas.length === 1 ? 'rubrica' : 'rubricas'}`;

  const exportar = () =>
    exportarCsv(
      'revisao-acao-em-massa.csv',
      [
        'Código',
        'Descrição',
        'Empregador',
        'Tipo (antigo)',
        'Tipo (novo)',
        'Natureza (antiga)',
        'Natureza (nova)',
        'Incidências',
        'Grau de risco',
        'Grau de risco (novo)',
        'Resultado',
      ],
      linhas.map((linha) => {
        const item = itens?.get(linha.rubrica.idAnaliseRubrica);
        return [
          linha.rubrica.codigoRubrica,
          linha.rubrica.descricao,
          nomeEmpregador(linha.rubrica.cnpjEmpregador) ?? linha.rubrica.cnpjEmpregador,
          linha.tipo.antigo,
          linha.tipo.novo ?? 'mantido',
          linha.natureza.antigo,
          linha.natureza.novo ?? 'mantido',
          linha.incidencias
            .map((i) => `${i.sigla} ${i.antigo}${i.novo === undefined ? '' : ` → ${i.novo}`}`)
            .join(' | '),
          linha.rubrica.grauRisco,
          grauNovo(payload, item) ?? 'automático',
          item?.statusPreparacao ?? 'PENDENTE',
        ];
      }),
    );

  return (
    <SectionCard data-qa={dataQa} className="overflow-hidden p-0">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-agnes-border bg-agnes-bg-subtle px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-agnes-primary">
            Revisão da edição em massa | {total}
          </h2>
          <p className="mt-1 text-sm text-agnes-text-muted">
            Confira a seguir as alterações antes de confirmar.
          </p>
        </div>
        <p className="max-w-xs text-right text-sm text-agnes-text-muted">
          As informações a seguir são a prévia das alterações. Revise os impactos antes de
          confirmar.
        </p>
      </div>

      <div className="flex flex-col gap-3 p-5">
        <AgnesAlert
          dataQa={`${dataQa}-resumo`}
          icon={<Info className="h-4 w-4 text-agnes-primary" />}
          className="bg-agnes-primary/5"
        >
          <AgnesAlertContent dataQa={`${dataQa}-resumo`}>
            Você está alterando <b>{total}</b>
            {origem === ORIGEM_SELECAO_MULTIPLA ? (
              <>
                {' '}
                em <b>{ORIGEM_SELECAO_MULTIPLA.toLowerCase()}</b>
              </>
            ) : (
              <>
                {' '}
                vinculadas à descrição <b>{origem}</b>
              </>
            )}
            {campos.length ? (
              <>
                , atualizando os campos <b>{campos.join(', ')}</b>
              </>
            ) : (
              ' (nenhum campo selecionado)'
            )}
            . A descrição permanece intacta.
          </AgnesAlertContent>
        </AgnesAlert>

        {removidas.rubricas > 0 && (
          <AgnesAlert
            dataQa={`${dataQa}-removidas`}
            preset="warning"
            icon={<Layers className="h-4 w-4" />}
          >
            <AgnesAlertContent dataQa={`${dataQa}-removidas`}>
              <b>
                {removidas.rubricas.toLocaleString('pt-BR')}{' '}
                {removidas.rubricas === 1 ? 'rubrica' : 'rubricas'}
              </b>{' '}
              de{' '}
              <b>
                {removidas.naturezas.length}{' '}
                {removidas.naturezas.length === 1 ? 'natureza' : 'naturezas'}
              </b>{' '}
              ({removidas.naturezas.join(', ')}) {removidas.rubricas === 1 ? 'foi' : 'foram'}{' '}
              <b>{removidas.rubricas === 1 ? 'removida' : 'removidas'}</b> da ação e não{' '}
              {removidas.rubricas === 1 ? 'será alterada' : 'serão alteradas'}.
            </AgnesAlertContent>
          </AgnesAlert>
        )}

        {somenteAprovacao && (
          <AgnesAlert
            dataQa={`${dataQa}-aprovacao`}
            icon={<ShieldCheck className="h-4 w-4 text-agnes-success" />}
            className="bg-agnes-success-light"
          >
            <AgnesAlertContent dataQa={`${dataQa}-aprovacao`}>
              Nenhum campo foi alterado — esta ação apenas <b>aprova</b> as rubricas. O{' '}
              <b>grau de risco é recalculado</b> pela Agnes e o valor atual fica no histórico; a
              coluna Grau de risco mostra o valor que será aplicado.
            </AgnesAlertContent>
          </AgnesAlert>
        )}

        {previa.fase === 'preparando' && (
          <AgnesAlert
            dataQa={`${dataQa}-preparando`}
            icon={<AgnesSpinner size="sm" />}
            className="bg-agnes-primary/5"
          >
            <AgnesAlertContent dataQa={`${dataQa}-preparando`}>
              Preparando a prévia das alterações. Cada rubrica é validada antes da confirmação.
            </AgnesAlertContent>
          </AgnesAlert>
        )}

        {previa.fase === 'demorada' && (
          <AgnesAlert dataQa={`${dataQa}-demorada`} preset="warning">
            <AgnesAlertContent dataQa={`${dataQa}-demorada`}>
              A prévia está demorando mais que o esperado.{' '}
              <AgnesButton
                dataQa={`${dataQa}-atualizar`}
                variant="text"
                size="sm"
                onClick={onAtualizarPrevia}
              >
                Atualizar
              </AgnesButton>
            </AgnesAlertContent>
          </AgnesAlert>
        )}

        {previa.fase === 'falha' && (
          <AgnesAlert dataQa={`${dataQa}-falha`} preset="error">
            <AgnesAlertContent dataQa={`${dataQa}-falha`}>
              {previa.mensagem}{' '}
              <AgnesButton
                dataQa={`${dataQa}-tentar-novamente`}
                variant="text"
                size="sm"
                onClick={onAtualizarPrevia}
              >
                Tentar novamente
              </AgnesButton>
            </AgnesAlertContent>
          </AgnesAlert>
        )}

        {naoAptas > 0 && (
          <AgnesAlert
            dataQa={`${dataQa}-nao-aptas`}
            preset="warning"
            icon={<AlertTriangle className="h-4 w-4" />}
          >
            <AgnesAlertContent dataQa={`${dataQa}-nao-aptas`}>
              <b>
                {naoAptas.toLocaleString('pt-BR')}{' '}
                {naoAptas === 1 ? 'rubrica não está apta' : 'rubricas não estão aptas'}
              </b>{' '}
              e {naoAptas === 1 ? 'será ignorada' : 'serão ignoradas'} na confirmação. Passe o mouse
              sobre o resultado para ver o motivo.
            </AgnesAlertContent>
          </AgnesAlert>
        )}

        <div className="flex items-center justify-between gap-3 pt-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-agnes-primary">
            <Layers className="h-4 w-4" />
            Revisão da ação tomada
          </h3>
          <ExportButton
            dataQa={`${dataQa}-exportar`}
            variant="outlined"
            size="sm"
            onClick={exportar}
          />
        </div>

        <div
          data-qa={`${dataQa}-tabela-container`}
          className="max-h-[42vh] overflow-auto rounded-lg border border-agnes-border"
          role="region"
          aria-label="Revisão da ação em massa"
          tabIndex={0}
        >
          <table
            data-qa={`${dataQa}-tabela`}
            className="w-full min-w-[1000px] text-left text-sm text-agnes-text-tables"
          >
            <thead className="sticky top-0 z-10 bg-agnes-bg-table-header text-xs text-agnes-text-secondary">
              <tr>
                {[
                  'Código',
                  'Descrição',
                  'Tipo (antigo → novo)',
                  'Natureza (antigo → novo)',
                  'Incidências (antigo → novo)',
                  'Grau de risco',
                  'Resultado',
                ].map((coluna) => (
                  <th key={coluna} scope="col" className={TH}>
                    {coluna}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--tp-border)]">
              {linhas.map((linha) => {
                const { rubrica } = linha;
                const linhaQa = `${dataQa}-item-${rubrica.idAnaliseRubrica}`;
                const item = itens?.get(rubrica.idAnaliseRubrica);
                const empregador =
                  nomeEmpregador(rubrica.cnpjEmpregador) ?? rubrica.cnpjEmpregador ?? '—';
                return (
                  <tr key={rubrica.idAnaliseRubrica} data-qa={linhaQa}>
                    <td className={`${TD} font-bold text-agnes-text-primary`}>
                      {rubrica.codigoRubrica || '—'}
                    </td>
                    <td className={TD}>
                      <p className="text-agnes-text-primary">{rubrica.descricao || '—'}</p>
                      <p className="text-xs uppercase text-agnes-text-muted" title={empregador}>
                        {truncar(empregador, LIMITE_EMPREGADOR)}
                      </p>
                    </td>
                    <td data-qa={`${linhaQa}-tipo`} className={`${TD} text-xs`}>
                      <DePara valor={linha.tipo} />
                    </td>
                    <td data-qa={`${linhaQa}-natureza`} className={`${TD} font-mono text-xs`}>
                      <DePara valor={linha.natureza} />
                    </td>
                    <td data-qa={`${linhaQa}-incidencias`} className={TD}>
                      <Incidencias linha={linha} />
                    </td>
                    <td data-qa={`${linhaQa}-risco`} className={TD}>
                      <GrauRisco
                        dataQa={`${linhaQa}-risco`}
                        atual={rubrica.grauRisco}
                        novo={grauNovo(payload, item)}
                        calculando={calculando}
                      />
                    </td>
                    <td className={TD}>
                      <Resultado dataQa={`${linhaQa}-resultado`} item={item} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-agnes-border px-5 py-4">
        <AgnesButton
          dataQa={`${dataQa}-voltar`}
          variant="text"
          startIcon={<ChevronLeft className="h-4 w-4" />}
          disabled={confirmando}
          onClick={onVoltar}
        >
          Voltar
        </AgnesButton>
        <AgnesButton
          dataQa={`${dataQa}-confirmar`}
          variant="primary"
          startIcon={<Check className="h-4 w-4" />}
          loading={confirmando}
          disabled={!podeConfirmar}
          onClick={onConfirmar}
        >
          {confirmando ? 'Aplicando alterações…' : 'Confirmar alterações'}
        </AgnesButton>
      </div>
    </SectionCard>
  );
}
